import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Clock, MessageCircle, Globe, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';
import { getPublicSiteUrl } from '@/lib/seo';
import { getLocaleFromPathname, buildPathForLocale, canonicalHomeUrl } from '@/lib/intl-seo';
import {
  breadcrumbListJsonLd,
  organizationJsonLd,
  contactPointsFromApiContacts,
  jsonLdScript,
} from '@/lib/schema-org';

const ContactPage = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const site = getPublicSiteUrl();
  const langCode =
    lang || getLocaleFromPathname(location.pathname) || (i18n.language || 'en').substring(0, 2);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      // Use direct fetch for public endpoint to avoid CORS issues with credentials
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/contact-info`);
      const data = await response.json();
      setContacts(data);
    } catch (error) {
      console.error('Contact info fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = selectedCountry === 'all' 
    ? contacts 
    : contacts.filter(c => c.country_code === selectedCountry);

  const activeCountries = contacts.filter(c => c.is_active !== false);

  const contactMetaDesc = t(
    'seo.contactDescription',
    'Turkish E-Market iletişim — ülke bazlı telefon, e-posta ve adres.',
  );
  const contactTitle = `${t('contact.title')} | Turkish E-Market`;
  const canonicalUrl = site
    ? `${site.replace(/\/$/, '')}${buildPathForLocale(langCode, ['contact'])}`
    : '';

  const breadcrumbItems = useMemo(
    () => [
      { label: t('common.home', 'Ana Sayfa'), to: buildPathForLocale(langCode, []) },
      { label: t('contact.title'), to: null },
    ],
    [t, langCode],
  );

  const breadcrumbLd = useMemo(() => {
    if (!site) return '';
    const items = [
      { name: t('common.home', 'Ana Sayfa'), item: canonicalHomeUrl(site, langCode) },
      { name: t('contact.title'), item: canonicalUrl },
    ];
    return jsonLdScript(breadcrumbListJsonLd(items));
  }, [site, langCode, t, canonicalUrl]);

  const organizationLd = useMemo(() => {
    if (!site) return '';
    const cps = contactPointsFromApiContacts(contacts);
    return jsonLdScript(organizationJsonLd({ site, contactPoints: cps }));
  }, [site, contacts]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Helmet htmlAttributes={{ lang: langCode }}>
          <title>{contactTitle}</title>
          <meta name="description" content={contactMetaDesc} />
          {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        </Helmet>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="contact-page" data-seo-ready="true">
      <Helmet htmlAttributes={{ lang: langCode }}>
        <title>{contactTitle}</title>
        <meta name="description" content={contactMetaDesc} />
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        {breadcrumbLd ? <script type="application/ld+json">{breadcrumbLd}</script> : null}
        {organizationLd ? <script type="application/ld+json">{organizationLd}</script> : null}
      </Helmet>
      <div className="container mx-auto px-4 pt-6">
        <PageBreadcrumb items={breadcrumbItems} />
      </div>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4 py-16 text-center">
          <Globe className="w-16 h-16 mx-auto mb-4 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('contact.title')}</h1>
          <p className="text-lg text-red-100 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </div>

      {/* Country Filter */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('contact.selectCountry')}</h2>
              <p className="text-gray-500 text-sm mt-1">
                {t('contact.servingCountries', { count: activeCountries.length })}
              </p>
            </div>
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-full md:w-72">
                <SelectValue placeholder={t('contact.selectCountryPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {t('contact.allCountries')}
                  </span>
                </SelectItem>
                {activeCountries.map(contact => (
                  <SelectItem key={contact.country_code} value={contact.country_code}>
                    <span className="flex items-center gap-2">
                      <span>{contact.flag}</span>
                      {t(`countries.${contact.country_code}`)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Contact Cards */}
        {filteredContacts.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {t('contact.noContactFound')}
            </h3>
            <p className="text-gray-500">
              {t('contact.noContactDesc')}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContacts.map(contact => (
              <div 
                key={contact.country_code} 
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`contact-card-${contact.country_code}`}
              >
                {/* Country Header */}
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{contact.flag}</span>
                    <div>
                      <h3 className="font-bold text-lg">{t(`countries.${contact.country_code}`)}</h3>
                      <p className="text-gray-300 text-sm">{contact.country_code}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="p-5 space-y-4">
                  {contact.phone && (
                    <a 
                      href={`tel:${contact.phone.replace(/\s/g, '')}`}
                      className="flex items-start gap-3 text-gray-700 hover:text-red-600 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                        <Phone className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('contact.phone')}</p>
                        <p className="font-medium">{contact.phone}</p>
                      </div>
                    </a>
                  )}

                  {contact.whatsapp && (
                    <a 
                      href={`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 text-gray-700 hover:text-green-600 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
                        <MessageCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('contact.whatsapp')}</p>
                        <p className="font-medium">{contact.whatsapp}</p>
                      </div>
                    </a>
                  )}

                  {contact.email && (
                    <a 
                      href={`mailto:${contact.email}`}
                      className="flex items-start gap-3 text-gray-700 hover:text-blue-600 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('contact.email')}</p>
                        <p className="font-medium break-all">{contact.email}</p>
                      </div>
                    </a>
                  )}

                  {contact.address && (
                    <div className="flex items-start gap-3 text-gray-700">
                      <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('contact.address')}</p>
                        <p className="font-medium">{contact.address}</p>
                      </div>
                    </div>
                  )}

                  {contact.working_hours && (
                    <div className="flex items-start gap-3 text-gray-700">
                      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('contact.workingHours')}</p>
                        <p className="font-medium">{contact.working_hours}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="p-4 pt-0 flex gap-2">
                  {contact.phone && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => window.open(`tel:${contact.phone.replace(/\s/g, '')}`, '_self')}
                    >
                      <Phone className="w-4 h-4 mr-1" />
                      {t('contact.call')}
                    </Button>
                  )}
                  {contact.whatsapp && (
                    <Button 
                      size="sm" 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => window.open(`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}`, '_blank')}
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      {t('contact.whatsapp')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 bg-white rounded-xl shadow-sm p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('contact.needHelp')}</h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            {t('contact.needHelpDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" className="gap-2">
              <Mail className="w-4 h-4" />
              destek@turkishemarket.com
            </Button>
            <Button className="gap-2 bg-red-600 hover:bg-red-700">
              <Phone className="w-4 h-4" />
              +90 850 123 45 67
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
